import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import { Channels, defaultOfficeOptions, Offices } from 'constants/enums'
import { toast } from 'react-toastify'
import { getCommercialStructureData } from 'services/shared'
import {
  CommercialStructureApi,
  Office,
  Regional,
  Zone
} from 'services/shared/commercialStructure/interfaces'
import {
  getYearMonthQuota,
  getActiveQuotaMonthsByOffice
} from 'services/shared/quotaMonth'
import { QuotaMonthType } from 'services/shared/quotaMonth/interfaces'
import { AppThunk } from 'store/store'

type InitialStateType = {
  loadingCommercialStructure: boolean
  loadingQuotas: boolean
  commercialStructure: CommercialStructureApi[]
  offices: Office[]
  regionals: Regional[]
  zones: Zone[]
  quotas: QuotaMonthType[]
}

const initialState: InitialStateType = {
  loadingCommercialStructure: false,
  loadingQuotas: false,
  commercialStructure: [] as CommercialStructureApi[],
  offices: [] as Office[],
  regionals: [] as Regional[],
  zones: [] as Zone[],
  quotas: [] as QuotaMonthType[]
}

const shared = createSlice({
  name: 'shared',
  initialState,
  reducers: {
    setLoadingCommercialStructure(state, action: PayloadAction<boolean>) {
      state.loadingCommercialStructure = action.payload
    },
    setCommercialStructure(state, action: PayloadAction<CommercialStructureApi[]>) {
      state.commercialStructure = action.payload
    },
    setOffices(state, action: PayloadAction<Office[]>) {
      state.offices = action.payload
    },
    setRegionals(state, action: PayloadAction<Regional[]>) {
      state.regionals = action.payload
    },
    setZones(state, action: PayloadAction<Zone[]>) {
      state.zones = action.payload
    },
    setLoadingQuotas(state, action: PayloadAction<boolean>) {
      state.loadingQuotas = action.payload
    },
    setQuotas(state, action: PayloadAction<QuotaMonthType[]>) {
      state.quotas = action.payload
    }
  }
})

export const {
  setLoadingCommercialStructure,
  setCommercialStructure,
  setOffices,
  setRegionals,
  setZones,
  setLoadingQuotas,
  setQuotas
} = shared.actions

export const sharedContext = shared.reducer

export const getCommercialStructure = (): AppThunk => async (dispatch) => {
  dispatch(setLoadingCommercialStructure(true))

  try {
    const commercialStructure = await getCommercialStructureData()

    dispatch(setCommercialStructure(commercialStructure))
    dispatch(setCommercialStructureOffices())
    dispatch(setCommercialStructureRegionals())
    dispatch(setCommercialStructureZones())
  } catch (error) {
    toast.error('Erro ao carregar a Estrutura comercial.')
  } finally {
    dispatch(setLoadingCommercialStructure(false))
  }
}

export const getQuotas = (): AppThunk => async (dispatch) => {
  dispatch(setLoadingQuotas(true))

  try {
    const quotas = await getYearMonthQuota()

    dispatch(setQuotas(quotas))
  } catch (error) {
    toast.error('Erro ao carregar quotas.')
  } finally {
    dispatch(setLoadingQuotas(false))
  }
}

export const getQuotasByOffice =
  (office: Offices): AppThunk =>
  async (dispatch) => {
    dispatch(setLoadingQuotas(true))

    try {
      const quotas = await getActiveQuotaMonthsByOffice(office)

      dispatch(setQuotas(quotas))
    } catch (error) {
      toast.error('Erro ao carregar quotas.')
    } finally {
      dispatch(setLoadingQuotas(false))
    }
  }

const setCommercialStructureOffices = (): AppThunk => async (dispatch, getState) => {
  const {
    shared: { commercialStructure }
  } = getState()

  const offices = commercialStructure.map((office) => ({
    id: office.CodigoEscritorio,
    description: office.DescricaoEscritorio,
    idMarket: defaultOfficeOptions.find(
      (officeOption) => officeOption.label === office.CodigoEscritorio
    )!.market as Channels
  })) as Office[]

  dispatch(setOffices(offices))
}

const setCommercialStructureRegionals = (): AppThunk => async (dispatch, getState) => {
  const {
    shared: { commercialStructure }
  } = getState()

  const regionals = commercialStructure
    .flatMap((office) => office.Equipes) // NOTE: Regionais = Equipes
    .map((regional) => ({
      id: regional.Codigo,
      description: regional.Descricao,
      idOffice: commercialStructure.find((office) => office.Equipes.includes(regional))!
        .CodigoEscritorio as Offices
    }))

  dispatch(setRegionals(regionals))
}

const setCommercialStructureZones = (): AppThunk => async (dispatch, getState) => {
  const {
    shared: { commercialStructure }
  } = getState()

  const zones = commercialStructure
    .flatMap((office) => office.Equipes) // NOTE: Regionais = Equipes
    .flatMap((regional) => regional.Regioes) // NOTE: Zonas = Regioes
    .map((zone) => ({
      id: zone.Codigo,
      description: zone.Descricao,
      idRegional: commercialStructure
        .flatMap((office) => office.Equipes)
        .find((regional) => regional.Regioes.includes(zone))!.Codigo
    }))

  const uniqueZones = removeDuplicatedZones(zones)

  dispatch(setZones(uniqueZones))
}

const removeDuplicatedZones = (
  items: { id: string; description: string; idRegional: string }[]
) => {
  const uniqueZones = items.filter(
    (item, index, self) =>
      index ===
      self.findIndex(
        (t) =>
          t.id === item.id &&
          t.description === item.description &&
          t.idRegional === item.idRegional
      )
  )

  return uniqueZones
}
